import { useEffect, useState } from "react";
import { DiagramType, CartConfigType, PartsType, Platform, RealtimeData, ShopTheme, ShopEuModelsConfig } from "../types/types";
import { fetchInitialPartsData, LoaderDataType, postAC, postDiagramSelected, postModelSelected, postTypeSelected } from "../api/ApiService";
import { addToCard, openDetailPageOfProduct } from "../userEvents/navigationEvents";
import { readLocationHashStorage, saveLocationHash } from "../services/local-storage-service";
import { getLocationHash } from "../utils/locationHashUtils";
import { useTranslation } from "react-i18next";

const getLocationParam = () => {
  if (!window) {
    return null;
  }

  const url = new URL(window.location + "");
  const { searchParams } = url;
  const location = searchParams.get("location");

  return location;
};

const readLocationFromLocalStorageIfNeeded = (
  locationStr: string | null,
  forceBrand: string | undefined,
  updateLocationHash: (locationStr: string) => void
) => {
  // if we have a locationStr or forceBrand is enable, we don't need to read from local storage
  if (locationStr || forceBrand) {
    return locationStr;
  }

  const newLocationStr = readLocationHashStorage();
  if (newLocationStr) {
    updateLocationHash(newLocationStr);
  }

  return newLocationStr;
};

type OverlayType = {
  message: string;
  type: "success" | "error" | "loading";
};

let postDiagramSelectedAbortController: AbortController | undefined = new AbortController();

export const usePartsFinder = ({ permanentDomain, country, forceBrand }: { permanentDomain: string; country: string; forceBrand?: string }) => {
  const [theme, setTheme] = useState<ShopTheme | undefined>();
  const [loadedInitialData, setLoadedInitialData] = useState();
  const [hasMonthlySubscription, setHasMonthlySubscription] = useState(false);
  const [models, setModels] = useState<Record<number, any> | undefined>();
  const [filteredModels, setFilteredModels] = useState<Record<number, any> | undefined>();
  const [diagrams, setDiagrams] = useState<Record<number, any> | undefined>();
  const [ownerManualLink, setOwnerManualLink] = useState<string | undefined>();
  const [parts, setParts] = useState<PartsType[]>();
  const [loadingParts, setLoadingParts] = useState(false);

  const [selectedBrand, setSelectedBrand] = useState<string | undefined>(forceBrand);
  const [selectedYear, setSelectedYear] = useState<string | undefined>();
  const [selectedTypeName, setSelectedTypeName] = useState<string | undefined>();
  const [selectedModelId, setSelectedModelId] = useState<string | undefined>();
  const [selectedDiagram, setSelectedDiagram] = useState<DiagramType | undefined>();
  const [showImagesInPartsFinder, setShowImagesInPartsFinder] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState<string | undefined>();
  const [cartConfig, setCartConfig] = useState<CartConfigType>({
    cartInStockColor: "#008001",
    cartOutOfStockColor: "#FFA500",
    cartExternalStockMessage: "In Stock at KTM",
    cartExternalStockMessageColor: "#000000",
  });
  const [shopAutoCreateProduct, setShopAutoCreateProduct] = useState(false);
  const [shopShowExternalStock, setShopShowExternalStock] = useState(false);
  const [shopPlatform, setShopPlatform] = useState<Platform | undefined>();
  const [overlay, setOverlay] = useState<OverlayType | undefined>();
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [euModelsConfig, setEuModelsConfig] = useState<ShopEuModelsConfig | undefined>();

  const { t } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        let locationStr: string | null = getLocationParam();
        locationStr = readLocationFromLocalStorageIfNeeded(locationStr, forceBrand, updateLocationHash);

        const res: LoaderDataType | null = await fetchInitialPartsData(permanentDomain, country, forceBrand, locationStr);
        if (!res) {
          return null;
        }

        if (locationStr) {
          const url = new URL(window.location.href);
          url.searchParams.set("location", locationStr);
          updateCanonicalUrl(url);
        }

        const preselectedData = res?.preselectedData;
        const loadedInitialData = res?.loadedInitialData;
        const hasMonthlySubscription = res?.hasMonthlySubscription;
        const platform: Platform = res?.shopPlatform;

        setHasMonthlySubscription(hasMonthlySubscription);
        setShowImagesInPartsFinder(res?.showImagesInPartsFinder);
        setShopAutoCreateProduct(res?.shopAutoCreateProduct || false);
        setShopShowExternalStock(res?.shopShowExternalStock || false);
        setLoadedInitialData(loadedInitialData);
        setTheme(res?.theme);
        setShopPlatform(platform);
        setSelectedBrand(forceBrand || preselectedData?.brandName);
        setTranslations(res?.translations || {});
        setCartConfig(res?.cartConfig || {});
        setEuModelsConfig(res?.shopEuModelsConfig);

        if (preselectedData) {
          const { models, diagrams, parts, typeName, year, modelId, diagramId, ownerManual } = preselectedData;

          if (typeName) {
            setSelectedTypeName(typeName);
          }

          if (year) {
            setSelectedYear(year);
          }

          if (modelId) {
            setSelectedModelId(modelId);
          }

          if (models) {
            setModels(models);
            setFilteredModels(res?.shopEuModelsConfig ? filterModels(models, res?.shopEuModelsConfig?.displayEuModelsList) : models);
          }

          if (ownerManual?.url) {
            setOwnerManualLink(ownerManual?.url);
          }

          if (diagrams) {
            setDiagrams(diagrams);
          }

          if (parts) {
            setParts(parts);
          }

          if (modelId && diagramId) {
            const diagram = diagrams[modelId]?.find((d: DiagramType) => d?.id === diagramId);

            if (diagram) {
              setSelectedDiagram(diagram);
            }
          }
        }
      } catch (e) {
        console.log(e);
        return null;
      }
    };

    fetchData();
  }, []);

  const setLocation = (brand?: string, year?: string, typeName?: string, modelId?: string, diagramId?: string) => {
    const hash = getLocationHash(brand, year, typeName, modelId, diagramId);

    if (hash) {
      updateLocationHash(hash);
    }

    // Save the hash to the local storage if the model is selected.
    if (brand && year && typeName && modelId) {
      saveLocationHash(getLocationHash(brand, year, typeName, modelId));
    } else {
      saveLocationHash(null);
    }
  };

  const updateLocationHash = (hash: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("location", hash);
    window.history.pushState(null, "", url);

    updateCanonicalUrl(url);
  };

  const updateCanonicalUrl = (url: URL) => {
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical !== null) {
      canonical instanceof HTMLLinkElement && (canonical.href = url.toString());
    }
  };

  const onBrandSelected = async (brand: string | undefined) => {
    setSelectedBrand(brand);
    setLocation(brand);

    setSelectedYear(undefined);
    setSelectedTypeName(undefined);
    setSelectedModelId(undefined);
    setSelectedDiagram(undefined);

    setDiagrams(undefined);
    setParts(undefined);
    setOwnerManualLink(undefined);
  };

  const onYearSelected = async (year: string) => {
    setLocation(selectedBrand, year);
    setSelectedYear(year);

    setSelectedTypeName(undefined);
    setSelectedModelId(undefined);
    setSelectedDiagram(undefined);

    setParts(undefined);
    setDiagrams(undefined);
    setModels(undefined);
    setFilteredModels(undefined);
    setOwnerManualLink(undefined);
  };

  const onTypeSelected = async (typeName: string) => {
    setSelectedTypeName(typeName);
    setSelectedModelId(undefined);
    setSelectedDiagram(undefined);

    setParts(undefined);
    setDiagrams(undefined);
    setOwnerManualLink(undefined);

    setLocation(selectedBrand, selectedYear, typeName);

    if (!selectedBrand || !selectedYear) {
      return;
    }

    const models = await postTypeSelected(permanentDomain, country, selectedBrand, selectedYear, typeName);
    const filteredModels = euModelsConfig ? filterModels(models, euModelsConfig.displayEuModelsList) : models;
    setModels(models);
    setFilteredModels(filteredModels);
  };

  const onModelSelected = async (modelId: string) => {
    setSelectedModelId(modelId);
    setSelectedDiagram(undefined);
    setParts(undefined);
    setOwnerManualLink(undefined);

    setLocation(selectedBrand, selectedYear, selectedTypeName, modelId);

    if (selectedBrand && selectedYear && selectedTypeName) {
      const { diagrams, ownerManualLink } = await postModelSelected(permanentDomain, country, selectedBrand, selectedYear, selectedTypeName, modelId);
      setDiagrams(diagrams);
      setOwnerManualLink(ownerManualLink);
    }
  };

  const onDiagramSelected = async (diagram: DiagramType) => {
    try {
      if (!selectedBrand || !selectedYear || !selectedTypeName || !selectedModelId || !diagram?.id) {
        return;
      }

      setLoadingParts(true);

      setSelectedDiagram(diagram);
      setLocation(selectedBrand, selectedYear, selectedTypeName, selectedModelId, diagram?.id);

      if (postDiagramSelectedAbortController) {
        postDiagramSelectedAbortController.abort();
      }

      postDiagramSelectedAbortController = new AbortController();

      const parts = await postDiagramSelected(
        postDiagramSelectedAbortController,
        permanentDomain,
        country,
        selectedBrand,
        selectedYear,
        selectedTypeName,
        selectedModelId,
        diagram.id
      );
      setParts(parts);
      setLoadingParts(false);
      postDiagramSelectedAbortController = undefined;
    } catch (e) {
      console.log(e);
      setParts([]);
      setLoadingParts(false);

      if (e instanceof Error && e.name === "AbortError") {
        setLoadingParts(true);
      }
    }
  };

  const onAddToCart = async (part: PartsType | undefined, realtimeData: RealtimeData | undefined, variantIdLong: string | undefined, quantity: string) => {
    if (!selectedBrand) {
      return;
    }

    if (shopAutoCreateProduct && !realtimeData && shopPlatform === "shopify") {
      setOverlay({
        message: t("add_to_cart_loading"),
        type: "loading",
      });
      const realtimeData = await postAC(permanentDomain, part);
      afterPostAC(part, realtimeData, selectedBrand);
    } else {
      addToCard(permanentDomain, selectedBrand, part, realtimeData, variantIdLong, quantity);
    }
  };

  const afterPostAC = async (part: PartsType | undefined, realtimeData: RealtimeData | null, selectedBrand: string) => {
    if (realtimeData) {
      addToCard(permanentDomain, selectedBrand, part, realtimeData, realtimeData.variantId, "1");

      const newParts = parts?.map((p: PartsType) => {
        if (p.id === part?.id) {
          p.realtimeData = realtimeData;
        }

        return p;
      });

      setParts(newParts);
      setOverlay(undefined);
    } else {
      setOverlay({
        message: "Something went wrong. Please try again or contact us.",
        type: "error",
      });

      setTimeout(() => {
        setOverlay(undefined);
      }, 4000);
    }
  };

  const openVariant = async (partRealdata: RealtimeData) => {
    openDetailPageOfProduct(partRealdata);
  };

  const openImageModal = (src: string) => {
    setImageModalUrl(src);
  };

  const closeImageModal = () => {
    setImageModalUrl(undefined);
  };

  const onShowEuModelsToggle = (newDisplayEuModelsList: boolean) => {
    setEuModelsConfig({
      showEuModelsToggle: euModelsConfig?.showEuModelsToggle || false,
      displayEuModelsList: newDisplayEuModelsList,
    });

    setFilteredModels(filterModels(models || {}, newDisplayEuModelsList));
    setParts(undefined);
    setDiagrams(undefined);
    setSelectedModelId(undefined);
    setSelectedDiagram(undefined);
  };

  const filterModels = (allModels: Record<number, any[]>, showEuModels: boolean) => {
    if (showEuModels) {
      return allModels;
    }

    const filteredModels: Record<number, any[]> = {};
    for (const key in allModels) {
      filteredModels[key] = [
        ...allModels[key].filter((model: any) => {
          return !model.name.includes("(EU)");
        }),
      ];
    }

    return filteredModels;
  };

  return {
    theme,
    models: filteredModels,
    diagrams,
    ownerManualLink,
    parts,
    loadingParts,
    loadedInitialData,
    hasMonthlySubscription,
    showImagesInPartsFinder,
    imageModalUrl,
    cartConfig,
    shopAutoCreateProduct,
    shopShowExternalStock,
    translations,
    // Selectors
    selectedBrand,
    selectedYear,
    selectedTypeName,
    selectedModelId,
    selectedDiagram,

    // overlay errors
    overlay,

    // EU models
    euModelsConfig,
    onShowEuModelsToggle,

    onBrandSelected,
    onTypeSelected,
    onYearSelected,
    onModelSelected,
    onDiagramSelected,
    onAddToCart,
    openVariant,
    openImageModal,
    closeImageModal,
  };
};
